import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  Grid,
  TextField,
  Button,
  Typography,
  Divider,
} from "@mui/material";
import DropdownSelector from "../../atoms/Common/DropdownSelector";
import LabeledCheckbox from "../../atoms/Inputs/LabeledCheckbox.tsx";
import MyDatePicker from "../../atoms/Common/MyDatePicker";
import { getClientList } from "../../api/client";
import { useSnack } from "../../services/SnakbarProvider";
import {
  createClientPurchaseOrder,
  getClientPurchaseOrderDetail,
  updateClientPurchaseOrder,
} from "../../api/clientPurchaseOrders";
import { handleError } from "../../services/errorHandler";
import { getCurrencyList } from "../../api/common/currencies";
import { getCatalogue } from "../../api/common/catalogues.ts";
import CPODetails, { emptyDetail } from "../../molecules/CPO/CPODetails";
import dayjs from "dayjs";

const validationSchema = Yup.object({
  clientId: Yup.number().required("Cliente requerido"),
  code: Yup.string().required("Código requerido"),
  currencyId: Yup.number(),
  comments: Yup.string(),
  isAGS: Yup.boolean(),
  details: Yup.array()
    .of(
      Yup.object({
        finishedGoodId: Yup.number().required("Producto requerido"),
        quantity: Yup.number().min(1, "Cantidad requerida"),
      })
    )
    .min(1, "Agregue al menos una línea"),
});

const toDateInput = (value) =>
  value ? dayjs(value).format("YYYY-MM-DD") : null;

const initialValues = {
  clientId: "",
  code: "",
  currencyId: "",
  receptionDate: null,
  requestedDeliveryDate: null,
  firstMad: null,
  lastMad: null,
  rfid: false,
  isAGS: false,
  destinationId: "",
  comments: "",
  details: [emptyDetail()],
};

const CPOForm = ({ id, onSubmit, companyId }) => {
  const formMode = id ? "edit" : "insert";
  const [loading, setLoading] = useState(false);
  const { openSnack } = useSnack();
  const [formData, setFormData] = useState(initialValues);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getClientPurchaseOrderDetail(id);
      const data = res.data || {};
      setFormData({
        ...initialValues,
        ...data,
        destinationId: data.destinationId ?? "",
        currencyId: data.currencyId ?? "",
        details:
          data.details?.length > 0
            ? data.details.map((d) => ({
                id: d.id,
                finishedGoodId: d.finishedGoodId,
                finishedGood: d.finishedGood || null,
                workOrder: d.workOrder ?? "",
                code: d.code ?? 0,
                quantity: d.quantity ?? 0,
                unitPrice: d.unitPrice ?? 0,
                pallets: d.pallets ?? 0,
                remains: d.remains ?? 0,
              }))
            : [emptyDetail()],
      });
    } catch (e) {
      openSnack(handleError(e), "error");
    }
    setLoading(false);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const details = (values.details || []).map((d) => {
        const line = {
          finishedGoodId: Number(d.finishedGoodId),
          workOrder: d.workOrder || "",
          code: Number(d.code) || 0,
          quantity: Number(d.quantity) || 0,
          unitPrice: Number(d.unitPrice) || 0,
          pallets: Number(d.pallets) || 0,
          remains: Number(d.remains) || 0,
        };
        if (d.id) line.id = d.id;
        return line;
      });

      const payload = {
        companyId,
        clientId: Number(values.clientId),
        code: values.code,
        currencyId: Number(values.currencyId) || 0,
        receptionDate: toDateInput(values.receptionDate),
        requestedDeliveryDate: toDateInput(values.requestedDeliveryDate),
        firstMad: toDateInput(values.firstMad),
        lastMad: toDateInput(values.lastMad),
        rfid: Boolean(values.rfid),
        isAGS: Boolean(values.isAGS),
        destinationId:
          values.destinationId === "" || values.destinationId == null
            ? null
            : Number(values.destinationId),
        comments: values.comments || "",
        details,
      };

      if (values.rfidSupplierId) {
        payload.rfidSupplierId = values.rfidSupplierId;
      }

      if (formMode === "insert") {
        await createClientPurchaseOrder(payload);
        openSnack("CPO creado correctamente", "success");
      }
      if (formMode === "edit") {
        await updateClientPurchaseOrder(id, payload);
        openSnack("CPO actualizado correctamente", "success");
      }
      onSubmit();
    } catch (e) {
      openSnack(handleError(e), "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (formMode === "edit") {
      fetchData();
    } else {
      setFormData(initialValues);
    }
  }, []);

  return (
    <Formik
      initialValues={{ ...formData }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ touched, errors, values, setFieldValue }) => (
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h4">Datos Generales</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Field
                name="clientId"
                label="Cliente"
                as={DropdownSelector}
                fetchFunction={getClientList}
                fetchParams={{ companyId: companyId, pageSize: 1000 }}
                error={touched.clientId && Boolean(errors.clientId)}
                helperText={touched.clientId && errors.clientId}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Field
                fullWidth
                name="code"
                label="Orden de Compra"
                as={TextField}
                error={touched.code && Boolean(errors.code)}
                helperText={touched.code && errors.code}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Field
                name="currencyId"
                label="Moneda"
                as={DropdownSelector}
                fetchFunction={getCurrencyList}
                error={touched.currencyId && Boolean(errors.currencyId)}
                helperText={touched.currencyId && errors.currencyId}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MyDatePicker
                fullWidth
                slotProps={{ textField: { fullWidth: true } }}
                label="Fecha de recepción"
                value={
                  values.receptionDate ? dayjs(values.receptionDate) : null
                }
                onChange={(value) => setFieldValue("receptionDate", value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MyDatePicker
                fullWidth
                slotProps={{ textField: { fullWidth: true } }}
                label="Entrega solicitada"
                value={
                  values.requestedDeliveryDate
                    ? dayjs(values.requestedDeliveryDate)
                    : null
                }
                onChange={(value) =>
                  setFieldValue("requestedDeliveryDate", value)
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MyDatePicker
                fullWidth
                slotProps={{ textField: { fullWidth: true } }}
                label="First MAD"
                value={values.firstMad ? dayjs(values.firstMad) : null}
                onChange={(value) => setFieldValue("firstMad", value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MyDatePicker
                fullWidth
                slotProps={{ textField: { fullWidth: true } }}
                label="Last MAD"
                value={values.lastMad ? dayjs(values.lastMad) : null}
                onChange={(value) => setFieldValue("lastMad", value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Field
                name="destinationId"
                label="Destino"
                as={DropdownSelector}
                fetchFunction={getCatalogue}
                fetchParams={{
                  catalogueName: "DESTINATION",
                  pageSize: 1000,
                }}
                optionLabel={["code", "name"]}
                optionSeparator=" | "
              />
            </Grid>

            <Grid item xs={12}>
              <Field
                fullWidth
                name="comments"
                label="Comentarios"
                multiline
                rows={3}
                as={TextField}
              />
            </Grid>

            <Grid item xs={6}>
              <Field name="rfid" label="RFID" as={LabeledCheckbox} />
            </Grid>
            <Grid item xs={6}>
              <Field name="isAGS" label="Es AGS" as={LabeledCheckbox} />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <CPODetails companyId={companyId} />
              {typeof errors.details === "string" && (
                <Typography color="error" variant="caption">
                  {errors.details}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Button type="submit" variant="contained" disabled={loading}>
                {formMode === "edit" ? "Actualizar" : "Añadir"}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default CPOForm;
